import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Loader2, ArrowLeft, Upload, CheckCircle2, Send, Download, AlertTriangle,
  Eye, MessageSquare, Archive, RotateCcw, Pencil, Sparkles, ChevronDown, History,
} from 'lucide-react'
import api from '../../services/api'
import { formatDate } from '../../utils/helpers'
import FileViewerModal from '../common/FileViewerModal.jsx'
import PlatformBanner from './PlatformBanner.jsx'
import { themeFor, themeVars, productLabel } from './dashboardTheme.js'
import { commentColors, initialsOf } from '../../utils/docComments.js'
import { useTranslation } from 'react-i18next'

/**
 * ProductRoom — one country's working space: its files and its conversation.
 *
 * This is ProTrack's own Document Flow, running for another product's people. Deliberately the
 * same screen: open a file in the in-app viewer, select a passage and comment on that exact
 * spot, reply, read the summary sheet, upload a new version. Somebody who has used ProTrack's
 * documents already knows how to use this, and a second dialect of the same idea would be a
 * thing to learn for no reason.
 *
 * What the rooms add is the gate. A file cannot be submitted until a person has read it, written
 * what it is, and put their name to that.
 */
export default function ProductRoom() {
  const { t } = useTranslation('platform')
  const { t: tc } = useTranslation('common')
  const { productKey, countryIso } = useParams()
  const navigate = useNavigate()
  const base = `/platform/products/${productKey}/rooms/${countryIso}`

  const [room, setRoom] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [expanded, setExpanded] = useState(null)          // doc id

  const [viewer, setViewer] = useState(null)              // {doc, version}
  const [quotedText, setQuotedText] = useState('')        // the passage a comment anchors to
  const [replyTo, setReplyTo] = useState(null)            // {id, user_name}
  const [activeAnchor, setActiveAnchor] = useState(null)  // comment whose passage is emphasized
  const [editingComment, setEditingComment] = useState(null)
  const [editCommentText, setEditCommentText] = useState('')
  const [newComment, setNewComment] = useState('')
  const [viewerComment, setViewerComment] = useState('')
  const [commentBusy, setCommentBusy] = useState(false)

  const [note, setNote] = useState('')                    // the room's own thread
  const bottom = useRef(null)

  const captureSelection = () => {
    const sel = (typeof window !== 'undefined' && window.getSelection) ? String(window.getSelection()) : ''
    if (sel && sel.trim()) setQuotedText(sel.trim().slice(0, 2000))
  }

  const load = async (archived = showArchived) => {
    try {
      const r = await api.get(base, { params: archived ? { include_archived: true } : {} })
      setRoom(r.data)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not open this room.')
    }
  }
  useEffect(() => { load() }, [productKey, countryIso])
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }) }, [room?.messages?.length])

  const commentsFor = (docId) => (room?.messages || []).filter(m => m.doc_id === docId)
  const canEditComment = (c) => c.user_id === room?.me
  const canDeleteComment = (c) => c.user_id === room?.me || room?.may_submit

  const post = async (path, body, fallback) => {
    setBusy(true); setError(null)
    try { await api.post(`${base}${path}`, body); await load() ; return true }
    catch (e) { setError(e.response?.data?.detail || fallback); return false }
    finally { setBusy(false) }
  }

  const postComment = async (doc, text, fromViewer) => {
    const content = (text || '').trim()
    if (!content) return
    setCommentBusy(true)
    const ok = await post('/messages', {
      body: content, doc_id: doc.id,
      quoted_text: quotedText || null,
      parent_id: replyTo?.id || null,
      version_no: viewer?.version || doc.current_version || null,
    }, 'Could not post that comment.')
    setCommentBusy(false)
    if (ok) {
      if (fromViewer) setViewerComment(''); else setNewComment('')
      setQuotedText(''); setReplyTo(null)
    }
  }

  const startEditComment = (c) => { setEditingComment(c.id); setEditCommentText(c.content) }
  const cancelEditComment = () => { setEditingComment(null); setEditCommentText('') }
  const saveEditComment = async (c) => {
    if (await post(`/messages/${c.id}/edit`, { body: editCommentText }, 'Could not save that.')) cancelEditComment()
  }
  const deleteComment = async (c) => { await post(`/messages/${c.id}/delete`, undefined, 'Could not delete that.') }

  // The server's reason arrives as a Blob because we asked for one — read it back, or a real
  // explanation ("the stored file couldn't be read") is replaced by a shrug.
  const download = async (doc, version) => {
    try {
      const r = await api.get(`${base}/documents/${doc.id}/download${version ? `?version=${version}` : ''}`,
                              { responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.latest?.original_filename || doc.title
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      let detail = null
      try { detail = JSON.parse(await e.response?.data?.text())?.detail } catch { /* not JSON */ }
      setError(detail || `Could not download that file${e.response?.status ? ` (${e.response.status})` : ''}.`)
    }
  }

  const openViewer = (doc, version) => {
    setViewer({ doc, version: version || doc.current_version })
    setQuotedText(''); setReplyTo(null); setActiveAnchor(null); cancelEditComment()
  }

  const toggleArchived = async () => {
    const next = !showArchived
    setShowArchived(next)
    await load(next)
  }

  const sayToRoom = async () => {
    const body = note.trim()
    if (!body) return
    if (await post('/messages', { body }, 'Could not post that.')) setNote('')
  }

  if (error && !room) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(`/products/${productKey}/rooms`)} className="text-xs text-gray-500 hover:text-[var(--pt-accent-dark,#4338CA)] inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> All rooms
        </button>
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      </div>
    )
  }
  if (!room) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Opening the room…</div>
  }

  const roomMessages = room.messages.filter(m => !m.doc_id)

  return (
    <div className="p-6 max-w-5xl mx-auto" style={themeVars(themeFor(productKey))}>
      <PlatformBanner theme={themeFor(productKey)} kicker={productLabel(productKey, room.product?.name)} title={room.country}
                      back={{ label: 'All rooms', onClick: () => navigate(`/products/${productKey}/rooms`) }}>
        {room.region} · your role here: <span className="font-semibold text-white">
          {room.my_role || 'none of your own — ProTrack Super Admin oversight'}</span>
        {room.may_submit ? ' — you can submit' : ' — your admin submits'}
      </PlatformBanner>

      {error && <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

      <UploadBox base={base} onDone={load} setError={setError} />

      <p className="mt-5 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2">
        <span>💡</span>
        <span>
          <b>{t('click_document')}</b> Inside, <b>select any text</b> to comment on that exact
          line · <b>reply</b> to discuss · <b>click a comment</b> to jump to its highlighted spot ·
          then write <b>what the file is</b> and approve it — nothing is submitted until somebody has.
        </span>
      </p>

      <div className="mt-5 flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">{t('documents')}</h2>
        {(room.archived_count > 0 || showArchived) && (
          <button onClick={toggleArchived} className="text-[11px] text-gray-500 hover:text-[var(--pt-accent-dark,#4338CA)] inline-flex items-center gap-1">
            <Archive className="w-3 h-3" />
            {showArchived ? 'Hide put-away files' : `Show ${room.archived_count} put away`}
          </button>
        )}
      </div>

      {room.documents.length === 0 ? (
        <div className="mt-2 bg-white border border-dashed border-gray-300 rounded-xl py-10 text-center">
          <p className="text-sm text-gray-500">{t('nothing_here_yet')}</p>
        </div>
      ) : (
        <div className="mt-2 space-y-3">
          {room.documents.map(doc => {
            const isOpen = expanded === doc.id
            const comments = commentsFor(doc.id)
            const roots = comments.filter(c => !c.parent_id)
            const repliesOf = (id) => comments.filter(c => c.parent_id === id)
            const editable = doc.status === 'draft' && !doc.archived_at
            const statusPill = doc.archived_at
              ? { t: doc.retention_until ? `put away · kept until ${formatDate(doc.retention_until)}` : 'put away', c: 'border-[var(--pt-accent,#4F46E5)]/30 bg-[var(--pt-accent-soft,#EEF2FF)] text-[var(--pt-accent-dark,#4338CA)]' }
              : doc.status === 'delivered' ? { t: 'delivered', c: 'border-green-200 bg-green-50 text-green-700' }
              : doc.status === 'submitted' ? { t: 'submitted — locked', c: 'border-amber-200 bg-amber-50 text-amber-700' }
              : { t: 'draft', c: 'border-gray-200 bg-gray-50 text-gray-600' }

            const { colorOf } = commentColors(comments, room.me)
            const renderComment = (c, isReply) => {
              const co = colorOf(c)
              return (
                <div key={c.id} className={`text-xs bg-white border border-gray-200 rounded px-2.5 py-1.5 ${isReply ? 'ml-4 mt-1 border-l-2 border-l-blue-200' : ''}`}>
                  {c.quoted_text && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[8px] font-bold text-white align-middle mr-1" style={{ backgroundColor: co.sw }}>{initialsOf(c.user_name)}</span>
                  )}
                  <span className="font-semibold text-gray-800">{c.user_name}</span>
                  <span className="text-gray-400">{c.version_no ? ` · v${c.version_no}` : ''}{c.created_at ? ` · ${formatDate(c.created_at)}` : ''}{c.edited_at ? ' · edited' : ''}</span>
                  {c.quoted_text && (
                    <blockquote className="mt-1 border-l-2 text-gray-600 italic px-2 py-0.5 rounded-r" style={{ borderColor: co.sw, backgroundColor: co.hl }}>
                      "{c.quoted_text}"
                    </blockquote>
                  )}
                  {editingComment === c.id ? (
                    <div className="mt-1">
                      <textarea rows={2} value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)}
                        className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                      <div className="flex gap-2 mt-1">
                        <button onClick={() => saveEditComment(c)} disabled={!editCommentText.trim()}
                          className="px-2 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">{tc('actions.save')}</button>
                        <button onClick={cancelEditComment} className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-700">{tc('actions.cancel')}</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{c.content}</p>
                  )}
                  {editingComment !== c.id && (
                    <div className="flex items-center gap-2 mt-0.5">
                      {!doc.archived_at && !isReply && (
                        <button onClick={() => { setExpanded(doc.id); setReplyTo({ id: c.id, user_name: c.user_name }) }}
                          className="text-[10px] text-blue-600 hover:underline">{t('reply')}</button>
                      )}
                      {!doc.archived_at && canEditComment(c) && (
                        <button onClick={() => startEditComment(c)} className="text-[10px] text-gray-500 hover:underline">{tc('actions.edit')}</button>
                      )}
                      {!doc.archived_at && canDeleteComment(c) && (
                        <button onClick={() => deleteComment(c)} className="text-[10px] text-red-500 hover:underline">{tc('actions.delete')}</button>
                      )}
                    </div>
                  )}
                  {repliesOf(c.id).map(r => renderComment(r, true))}
                </div>
              )
            }

            return (
              <div key={doc.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden transition-all">
                {/* Card header */}
                <div className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0 cursor-pointer group" onClick={() => openViewer(doc)} title={t('t_open_document')}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-gray-800 truncate group-hover:text-blue-700 group-hover:underline">{doc.title}</h4>
                      <span className="text-[10px] font-medium border border-teal-200 bg-teal-50 text-teal-700 rounded-full px-2 py-0.5">v{doc.current_version}</span>
                      <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${statusPill.c}`}>{statusPill.t}</span>
                      {editable && (
                        <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${doc.summary_approved
                          ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                          {doc.summary_approved ? '✓ summary approved' : 'needs a summary'}
                        </span>
                      )}
                      {doc.comment_count > 0 && (
                        <span className="text-[10px] text-gray-500 inline-flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {doc.comment_count}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {doc.latest?.original_filename}
                      {doc.latest?.file_size_bytes ? ` · ${(doc.latest.file_size_bytes / 1024 / 1024).toFixed(2)} MB` : ''}
                      {' · '}by {doc.uploaded_by || 'someone'}
                      {doc.latest?.created_at ? ` · ${formatDate(doc.latest.created_at)}` : ''}
                    </p>
                    {doc.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{doc.description}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => openViewer(doc)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                    ><Eye className="w-3.5 h-3.5" /> Open</button>
                    <button onClick={() => download(doc)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-700 border border-gray-200 rounded hover:bg-gray-50"
                    ><Download className="w-3.5 h-3.5" /></button>
                    {room.may_submit && editable && (
                      <button onClick={() => post(`/documents/${doc.id}/archive`, undefined, 'Could not put that away.')}
                        title={t('t_put_away')}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs text-[var(--pt-accent-dark,#4338CA)] border border-[var(--pt-accent,#4F46E5)]/30 bg-[var(--pt-accent-soft,#EEF2FF)] rounded hover:brightness-95"
                      ><Archive className="w-3.5 h-3.5" /></button>
                    )}
                    {room.may_submit && doc.archived_at && (
                      <button onClick={() => post(`/documents/${doc.id}/unarchive`, undefined, 'Could not restore that.')}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-700 border border-gray-200 rounded hover:bg-gray-50"
                      ><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
                    )}
                    <button onClick={() => setExpanded(e => (e === doc.id ? null : doc.id))} className="p-1.5 text-gray-400 hover:text-gray-700 rounded">
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* What happens next, on the face of the card. These are the actions the room
                    exists for — asking somebody to find a chevron first is asking them not to. */}
                {(editable || (doc.status === 'submitted' && !doc.archived_at)) && (
                  <div className="px-4 pb-3 -mt-0.5 space-y-2">
                    {editable && (
                      <div className="flex flex-wrap items-center gap-2">
                        <NewVersion base={`${base}/documents/${doc.id}`} onDone={load} setError={setError}
                                    label={`Re-upload — becomes v${(doc.current_version || 0) + 1}`} />
                        {room.may_submit && (doc.summary_approved ? (
                          <button onClick={() => post(`/documents/${doc.id}/submit`, undefined, 'Could not submit.')}
                            disabled={busy}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50 font-semibold"
                          >📤 {busy ? 'Submitting…' : 'Submit — this locks it'}</button>
                        ) : (
                          <button onClick={() => setExpanded(doc.id)}
                            className="px-2.5 py-1 text-xs border border-dashed border-amber-300 text-amber-800 bg-amber-50/60 rounded hover:bg-amber-100">
                            Write what this file is, then you can submit →
                          </button>
                        ))}
                        <RenameBox doc={doc} onSave={(body) => post(`/documents/${doc.id}/details`, body, 'Could not save that.')} />
                        {doc.versions.length > 1 && (
                          <button onClick={() => setExpanded(e => (e === doc.id ? null : doc.id))}
                            className="text-[11px] text-gray-500 hover:text-[var(--pt-accent-dark,#4338CA)] inline-flex items-center gap-1">
                            <History className="w-3 h-3" /> {doc.versions.length} versions
                          </button>
                        )}
                      </div>
                    )}
                    {doc.status === 'submitted' && !doc.archived_at && (
                      <div className="space-y-2">
                        {doc.delivery_error ? (
                          // Approved but not through. The lock stands — the room agreed, and a
                          // network problem is not a reason to reopen that.
                          <p className="text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5 flex gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                            <span><b>{t('approved_not_delivered')}</b> {doc.delivery_error}</span>
                          </p>
                        ) : (
                          <p className="text-[11px] text-amber-900 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" /> Locked and approved — sending.
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {room.may_submit && (
                            <button onClick={() => post(`/documents/${doc.id}/retry-delivery`, undefined, 'Could not send it again.')}
                              disabled={busy}
                              className="px-2.5 py-1 text-xs text-[var(--pt-accent-dark,#4338CA)] border border-[var(--pt-accent,#4F46E5)]/30 bg-[var(--pt-accent-soft,#EEF2FF)] rounded hover:brightness-95 disabled:opacity-50">
                              {busy ? 'Sending…' : 'Send it again'}
                            </button>
                          )}
                          {/* The by-hand route, still here: if the automatic push cannot work,
                              someone carries the file across and records that they did. */}
                          <button onClick={() => post(`/documents/${doc.id}/delivered`, undefined, 'Only the person who receives submissions can do that.')}
                            disabled={busy}
                            className="px-2.5 py-1 text-xs text-green-800 border border-green-300 bg-green-50 rounded hover:bg-green-100 disabled:opacity-50">
                            I uploaded it by hand — mark delivered
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {isOpen && (
                  <div className="border-t border-gray-100 px-4 py-3 space-y-4 bg-gray-50/50">
                    {/* ① The summary sheet — and the gate */}
                    <SummarySheet doc={doc} base={`${base}/documents/${doc.id}`} editable={editable}
                                  autoSummary={room.auto_summary} onDone={load} setError={setError} />

                    {doc.status === 'delivered' && (
                      <p className="text-[11px] text-green-800 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Delivered{doc.delivered_at ? ` ${new Date(doc.delivered_at).toLocaleString()}` : ''}
                        {doc.delivered_by ? ` by ${doc.delivered_by}` : ''}
                      </p>
                    )}

                    {/* ③ Version history */}
                    <div>
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
                        <History className="w-3 h-3" /> Version history
                      </p>
                      <div className="space-y-1">
                        {[...doc.versions].reverse().map(v => (
                          <div key={v.version} className="flex items-center justify-between gap-2 text-xs bg-white border border-gray-200 rounded px-2.5 py-1.5">
                            <span className="min-w-0 truncate text-gray-700">
                              <span className="font-semibold">v{v.version}</span> · {v.filename} · {v.uploaded_by || '—'}
                              {v.created_at ? <span className="text-gray-400"> · {formatDate(v.created_at)}</span> : null}
                            </span>
                            <span className="flex items-center gap-2 shrink-0">
                              <button onClick={() => openViewer(doc, v.version)} className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5">
                                <Eye className="w-3 h-3" /> View
                              </button>
                              <button onClick={() => download(doc, v.version)} className="text-gray-600 hover:text-gray-800"><Download className="w-3 h-3" /></button>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ④ Comments */}
                    <div>
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> Comments
                      </p>
                      <div className="space-y-1.5">
                        {roots.length ? roots.map(c => renderComment(c, false))
                          : <p className="text-xs text-gray-400">{t('no_comments')}</p>}
                      </div>
                      {doc.archived_at ? (
                        <p className="text-[11px] text-gray-400 mt-1.5">{t('put_away_readonly')}</p>
                      ) : (
                        <div className="mt-2">
                          {replyTo && expanded === doc.id && (
                            <div className="flex items-center justify-between text-[11px] bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-1">
                              <span className="text-blue-700">Replying to {replyTo.user_name}</span>
                              <button onClick={() => setReplyTo(null)} className="text-blue-500 hover:text-blue-700">✕</button>
                            </div>
                          )}
                          {quotedText && expanded === doc.id && (
                            <div className="flex items-start justify-between gap-2 text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
                              <span className="text-gray-600 italic truncate">💬 On: "{quotedText}"</span>
                              <button onClick={() => setQuotedText('')} className="text-amber-600 hover:text-amber-800 shrink-0">✕</button>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <textarea rows={1} value={expanded === doc.id ? newComment : ''}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder={replyTo ? 'Write a reply…' : 'Write a comment…'}
                              className="flex-1 text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                            <button onClick={() => { setExpanded(doc.id); captureSelection() }}
                              title="Select text in the document (or anywhere on the page), then click this to attach it to your comment"
                              className="px-2 py-1.5 text-xs border border-amber-300 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100"
                            >❝ Selection</button>
                            <button onClick={() => postComment(doc, newComment, false)}
                              disabled={commentBusy || !newComment.trim()}
                              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >{commentBusy ? '…' : (replyTo ? 'Reply' : 'Post')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* The room's own thread — about the country's work as a whole. Anything about one file
          belongs under that file, next to the thing being discussed. */}
      <h2 className="mt-8 text-base font-bold text-gray-900">{t('room_discussion')}</h2>
      <p className="text-[11px] text-gray-400">About this country's work as a whole. Talk about one file under that file.</p>
      <div className="mt-2 bg-white border border-gray-200 rounded-xl p-3 max-h-80 overflow-y-auto">
        {roomMessages.length === 0 ? (
          <p className="text-sm text-gray-400">{t('no_messages')}</p>
        ) : roomMessages.map(m => (
          <div key={m.id} className="py-2 border-b border-gray-50 last:border-0">
            <p className="text-[11px] text-gray-500">
              {m.user_name} · {new Date(m.created_at).toLocaleString()}{m.edited_at ? ' · edited' : ''}
            </p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{m.content}</p>
            {canDeleteComment(m) && (
              <button onClick={() => deleteComment(m)} className="text-[10px] text-red-500 hover:underline">{tc('actions.delete')}</button>
            )}
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <div className="mt-2.5 flex gap-2">
        <input value={note} onChange={e => setNote(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sayToRoom() } }}
               placeholder={t('ph_write_room')}
               className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <button onClick={sayToRoom} disabled={busy || !note.trim()}
                className="px-3 bg-[var(--pt-accent,#4F46E5)] hover:bg-[var(--pt-accent-dark,#4338CA)] disabled:opacity-40 text-white rounded-lg">
          <Send className="w-4 h-4" />
        </button>
      </div>

      {/* The in-app viewer, with the thread beside the document — read the file and discuss it
          in the same window, on the exact words in question. */}
      {viewer && (() => {
        const vDoc = room.documents.find(d => d.id === viewer.doc.id) || viewer.doc
        const all = commentsFor(vDoc.id)
        const roots = all.filter(c => !c.parent_id)
        const repliesOf = (id) => all.filter(c => c.parent_id === id)
        const canComment = !vDoc.archived_at
        const { colorOf, palette, highlights, legend } = commentColors(all, room.me)
        const activeComment = activeAnchor != null ? all.find(c => c.id === activeAnchor) : null
        const activeColor = activeComment ? colorOf(activeComment).hlStrong : undefined

        const renderComment = (c, isReply) => {
          const co = colorOf(c)
          return (
            <div key={c.id}
              onClick={c.quoted_text ? (e) => { e.stopPropagation(); setActiveAnchor(c.id) } : undefined}
              title={c.quoted_text ? 'Click to find & highlight this passage in the document' : undefined}
              className={`text-xs bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5 ${isReply ? 'ml-3 mt-1 border-l-2 border-l-blue-200' : ''} ${c.quoted_text ? 'cursor-pointer hover:border-amber-300' : ''} ${activeAnchor === c.id ? 'ring-2 ring-amber-400' : ''}`}>
              {c.quoted_text && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[8px] font-bold text-white align-middle mr-1" style={{ backgroundColor: co.sw }}>{initialsOf(c.user_name)}</span>
              )}
              <span className="font-semibold text-gray-800">{c.user_name}</span>
              <span className="text-gray-400">{c.version_no ? ` · v${c.version_no}` : ''}{c.created_at ? ` · ${formatDate(c.created_at)}` : ''}{c.edited_at ? ' · edited' : ''}</span>
              {c.quoted_text && (
                <blockquote className="mt-1 border-l-2 text-gray-600 italic px-2 py-0.5 rounded-r" style={{ borderColor: co.sw, backgroundColor: co.hl }}>
                  "{c.quoted_text}"
                </blockquote>
              )}
              {editingComment === c.id ? (
                <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                  <textarea rows={2} value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => saveEditComment(c)} disabled={!editCommentText.trim()}
                      className="px-2 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">{tc('actions.save')}</button>
                    <button onClick={cancelEditComment} className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-700">{tc('actions.cancel')}</button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{c.content}</p>
              )}
              {editingComment !== c.id && (
                <div className="flex items-center gap-2 mt-0.5">
                  {canComment && !isReply && (
                    <button onClick={(e) => { e.stopPropagation(); setReplyTo({ id: c.id, user_name: c.user_name }) }}
                      className="text-[10px] text-blue-600 hover:underline">{t('reply')}</button>
                  )}
                  {canComment && canEditComment(c) && (
                    <button onClick={(e) => { e.stopPropagation(); startEditComment(c) }} className="text-[10px] text-gray-500 hover:underline">{tc('actions.edit')}</button>
                  )}
                  {canComment && canDeleteComment(c) && (
                    <button onClick={(e) => { e.stopPropagation(); deleteComment(c) }} className="text-[10px] text-red-500 hover:underline">{tc('actions.delete')}</button>
                  )}
                </div>
              )}
              {repliesOf(c.id).map(r => renderComment(r, true))}
            </div>
          )
        }

        return (
          <FileViewerModal
            open={!!viewer}
            onClose={() => { setViewer(null); setViewerComment(''); setQuotedText(''); setReplyTo(null); setActiveAnchor(null); cancelEditComment() }}
            fetchUrl={`${base}/documents/${vDoc.id}/download${viewer.version ? `?version=${viewer.version}` : ''}`}
            filename={versionFilename(vDoc, viewer.version)}
            onDownload={() => download(vDoc, viewer.version)}
            onTextSelect={captureSelection}
            highlights={highlights}
            palette={palette}
            activeHighlightId={activeAnchor}
            activeColor={activeColor}
            sidePanel={(
              <>
                <div className="px-3 py-2.5 border-b border-gray-200 shrink-0">
                  <p className="text-xs font-semibold text-gray-700 inline-flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" /> Comments ({all.length})
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Select text to comment on it · click a comment to jump to its highlight.</p>
                  {legend.length > 0 && (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1.5">
                      {legend.map((l, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-[10px] text-gray-600">
                          <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: l.sw }} />{l.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
                  {roots.length ? roots.map(c => renderComment(c, false))
                    : <p className="text-xs text-gray-400 pt-2 text-center">{t('no_comments_start')}</p>}
                </div>
                {canComment ? (
                  <div className="p-2.5 border-t border-gray-200 shrink-0">
                    {replyTo && (
                      <div className="flex items-center justify-between text-[11px] bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-1">
                        <span className="text-blue-700 truncate">Replying to {replyTo.user_name}</span>
                        <button onClick={() => setReplyTo(null)} className="text-blue-500 hover:text-blue-700 shrink-0">✕</button>
                      </div>
                    )}
                    {quotedText && (
                      <div className="flex items-start justify-between gap-2 text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
                        <span className="text-gray-600 italic truncate">💬 On: "{quotedText}"</span>
                        <button onClick={() => setQuotedText('')} className="text-amber-600 hover:text-amber-800 shrink-0">✕</button>
                      </div>
                    )}
                    <textarea rows={2} value={viewerComment} onChange={(e) => setViewerComment(e.target.value)}
                      placeholder={replyTo ? 'Write a reply…' : (quotedText ? 'Comment on the selected text…' : 'Comment on this document…')}
                      className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none" />
                    <div className="flex gap-1.5 mt-1.5">
                      <button onMouseDown={(e) => { e.preventDefault(); captureSelection() }}
                        title="Select text in the document, then click to attach that passage to your comment"
                        className="px-2 py-1.5 text-xs border border-amber-300 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 shrink-0"
                      >❝ Selection</button>
                      <button onClick={() => postComment(vDoc, viewerComment, true)}
                        disabled={commentBusy || !viewerComment.trim()}
                        className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >{commentBusy ? 'Posting…' : (replyTo ? 'Reply' : 'Post comment')}</button>
                    </div>
                  </div>
                ) : (
                  <p className="p-2.5 border-t border-gray-200 text-[11px] text-gray-400 text-center shrink-0">
                    This document is put away — comments are read-only.
                  </p>
                )}
              </>
            )}
          />
        )
      })()}
    </div>
  )
}


// The filename the viewer needs to know which renderer to use — its extension decides whether a
// .docx is converted to HTML or a .pdf goes in a frame.
function versionFilename(doc, version) {
  const v = (doc.versions || []).find(x => x.version === version)
  return v?.filename || doc.latest?.original_filename || doc.title
}


/**
 * ① The summary sheet, and the gate.
 *
 * A draft is written from the file on upload; a person reads it, corrects whatever is wrong, and
 * approves it. Until then the document cannot be submitted — the approved words are what whoever
 * receives the file reads to know what they have.
 *
 * A new version withdraws the approval. The description was signed off against the file it
 * replaced, and carrying that forward would let a different document leave under a sign-off
 * nobody gave it.
 */
function SummarySheet({ doc, base, editable, autoSummary, onDone, setError }) {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('platform')
  const [text, setText] = useState(doc.summary || '')
  const [editing, setEditing] = useState(!doc.summary_approved)
  const [busy, setBusy] = useState(false)

  useEffect(() => { setText(doc.summary || ''); setEditing(!doc.summary_approved) },
            [doc.summary, doc.summary_approved])

  const approve = async () => {
    setBusy(true); setError(null)
    try { await api.post(`${base}/summary`, { summary: text }); await onDone(); setEditing(false) }
    catch (e) { setError(e.response?.data?.detail || 'Could not save that summary.') }
    finally { setBusy(false) }
  }

  const redraft = async () => {
    setBusy(true); setError(null)
    try {
      const r = await api.post(`${base}/summary/redraft`)
      setText(r.data?.summary || ''); setEditing(true); await onDone()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not write a new draft.')
    } finally { setBusy(false) }
  }

  // Locked, put away, or approved and not being changed — read it, don't edit it.
  if (!editable || (doc.summary_approved && !editing)) {
    return (
      <div className="bg-white border border-teal-100 rounded-lg p-3">
        <p className="text-[10px] font-semibold text-teal-700 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Summary sheet
          {doc.summary_approved && (
            <span className="ml-1 normal-case tracking-normal text-green-700 font-medium">
              ✓ approved{doc.summary_approved_by ? ` by ${doc.summary_approved_by}` : ''}
              {doc.summary_approved_at ? ` · ${formatDate(doc.summary_approved_at)}` : ''}
            </span>
          )}
        </p>
        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
          {doc.summary || 'No summary was written.'}
        </p>
        {editable && (
          <button onClick={() => setEditing(true)} className="mt-2 text-[11px] text-teal-700 hover:underline">{t('change_it')}</button>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white border border-amber-200 rounded-lg p-3">
      <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1 inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Summary sheet — needs approving
      </p>
      <p className="text-[11px] text-gray-500 mb-2">
        Open the file, read it, then say what it is. Nothing is submitted until somebody approves
        this — whoever receives the file reads your words to know what they have.
      </p>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
        placeholder="A couple of sentences: what the file contains, what period it covers, anything the reader needs to know."
        className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/20" />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <button onClick={approve} disabled={busy || text.trim().length < 40}
          className="px-2.5 py-1 text-xs text-green-700 border border-green-200 bg-green-50 rounded hover:bg-green-100 disabled:opacity-50 font-semibold">
          {busy ? 'Saving…' : '✓ Approve this summary'}
        </button>
        {autoSummary && (
          <button onClick={redraft} disabled={busy}
            className="flex items-center gap-1 px-2.5 py-1 text-xs text-[var(--pt-accent-dark,#4338CA)] border border-[var(--pt-accent,#4F46E5)]/30 bg-[var(--pt-accent-soft,#EEF2FF)] rounded hover:brightness-95 disabled:opacity-50">
            <Sparkles className="w-3 h-3" /> Write it again from the file (AI)
          </button>
        )}
        {doc.summary_approved && (
          <button onClick={() => { setText(doc.summary || ''); setEditing(false) }}
            className="text-[11px] text-gray-500 hover:underline">{tc('actions.cancel')}</button>
        )}
        <span className="text-[10px] text-gray-400">
          {text.trim().length < 40 ? `${40 - text.trim().length} more characters` : 'Long enough'}
        </span>
      </div>
    </div>
  )
}


function RenameBox({ doc, onSave }) {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('platform')
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(doc.title || '')
  const [description, setDescription] = useState(doc.description || '')

  if (!open) {
    return (
      <button onClick={() => { setTitle(doc.title || ''); setDescription(doc.description || ''); setOpen(true) }}
        className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-700 border border-gray-200 bg-white rounded hover:bg-gray-50">
        <Pencil className="w-3 h-3" /> Rename
      </button>
    )
  }
  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 space-y-2">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('ph_name')}
             className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs" />
      <input value={description} onChange={e => setDescription(e.target.value)} placeholder={t('ph_note_optional')}
             className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs" />
      <div className="flex gap-2">
        <button onClick={async () => { await onSave({ title, description }); setOpen(false) }} disabled={!title.trim()}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{tc('actions.save')}</button>
        <button onClick={() => setOpen(false)} className="text-[11px] text-gray-500 hover:underline">{tc('actions.cancel')}</button>
      </div>
    </div>
  )
}


/**
 * Add files — several at once if that is what somebody has.
 *
 * Each file becomes its own document with its own summary, discussion and version history,
 * because that is the unit that gets approved and sent. They are uploaded ONE REQUEST AT A TIME
 * rather than in one large multipart body: the gateway in front of us rejects anything much
 * over 50 MB, and a batch that fails as a whole tells you nothing about which file was the
 * problem.
 */
function UploadBox({ base, onDone, setError }) {
  const { t } = useTranslation('platform')
  const [title, setTitle] = useState('')
  const [files, setFiles] = useState([])
  const [progress, setProgress] = useState(null)   // {done, total} while uploading
  const pick = useRef(null)

  const send = async () => {
    if (!files.length) { setError('Choose a file first.'); return }
    setError(null)
    const failed = []
    for (let i = 0; i < files.length; i++) {
      setProgress({ done: i, total: files.length })
      const fd = new FormData()
      fd.append('file', files[i])
      // A typed title names ONE document. With several files each keeps its own name, because
      // five documents all called the same thing is worse than five filenames.
      fd.append('title', (files.length === 1 && title.trim()) || files[i].name)
      try {
        await api.post(`${base}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      } catch (e) {
        failed.push(`${files[i].name} — ${e.response?.data?.detail || 'upload failed'}`)
      }
    }
    setProgress(null)
    setTitle(''); setFiles([])
    if (pick.current) pick.current.value = ''
    // Whatever did land is kept. Naming the ones that did not beats discarding the lot.
    if (failed.length) setError(`${failed.length} of ${files.length} didn't upload: ${failed.join('; ')}`)
    await onDone()
  }

  const busy = !!progress
  return (
    <div className="mt-5 bg-white border border-gray-200 rounded-xl p-3.5">
      <p className="text-sm font-semibold text-gray-800 mb-2">{t('add_files')}</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={title} onChange={e => setTitle(e.target.value)}
               placeholder={files.length > 1 ? 'Each file keeps its own name' : 'What is it? (optional)'}
               disabled={files.length > 1}
               className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400" />
        <input ref={pick} type="file" multiple onChange={e => setFiles([...(e.target.files || [])])}
               className="text-sm file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-gray-100 file:text-sm" />
        <button onClick={send} disabled={busy || !files.length}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[var(--pt-accent,#4F46E5)] hover:bg-[var(--pt-accent-dark,#4338CA)] disabled:opacity-40 text-white text-sm font-semibold rounded-lg">
          <Upload className="w-4 h-4" />
          {busy ? `Uploading ${progress.done + 1} of ${progress.total}…`
                : files.length > 1 ? `Upload ${files.length} files` : 'Upload'}
        </button>
      </div>
      {files.length > 1 && (
        <p className="text-[11px] text-gray-500 mt-1.5">
          {files.map(f => f.name).join(' · ')}
        </p>
      )}
      <p className="text-[10px] text-gray-400 mt-1.5">
        Up to 25 MB each — pick as many as you like. Anyone in this room can upload. Every file
        gets its own summary to check, its own discussion and its own version history.
      </p>
    </div>
  )
}


function NewVersion({ base, onDone, setError, label }) {
  const [busy, setBusy] = useState(false)
  const pick = useRef(null)

  const send = async (f) => {
    if (!f) return
    setBusy(true); setError(null)
    const fd = new FormData()
    fd.append('file', f)
    try {
      await api.post(`${base}/versions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      await onDone()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not upload that version.')
    } finally { setBusy(false); if (pick.current) pick.current.value = '' }
  }

  return (
    <>
      <input ref={pick} type="file" className="hidden" onChange={e => send(e.target.files?.[0])} />
      <button onClick={() => pick.current?.click()} disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1 text-xs text-green-700 border border-green-200 bg-green-50 rounded hover:bg-green-100 disabled:opacity-50">
        <Upload className="w-3.5 h-3.5" /> {busy ? 'Uploading…' : (label || 'New version')}
      </button>
    </>
  )
}
